import React, {
  ChangeEvent,
  ComponentProps,
  useEffect,
  useRef,
  useState,
} from 'react';
import _ from 'lodash';

import {
  TextField,
  Box,
  TextFieldProps,
  InputLabel,
  SelectProps,
  TextareaAutosize,
  TextareaAutosizeProps,
  Select,
  FormControl,
  MenuItem,
  InputAdornment,
  ClickAwayListener,
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import styled from 'styled-components';
import { colors } from 'spw-theme';
import { omit, isNil } from 'ramda';
import { categoryBySpaceTypeId, asUsf } from 'spw-shared/utils';
import { US_STATES } from 'spw-us-states';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Category, Subcategory } from 'spw-redux/modules/space_types/types';
import { SpaceTypeCategoryMap } from 'spw-redux/modules/floorplan/types';
import {
  MAX_BUILDING_USF,
  CLIENT_NAMES_TOGGLE_CONTAINER_ID,
} from 'spw-constants';
import BaseTextField from './base-text-field';
import { DefaultChevron, DisabledChevron } from './base-select';

export const globalInputLabelProps = {
  shrink: true,
};

export const SPWInput = withStyles(
  ({ ...props }: TextFieldProps & { className?: string }) => {
    return (
      <TextField
        {...props}
        InputLabelProps={globalInputLabelProps}
        variant="outlined"
      />
    );
  },
  {
    root: {
      height: '40px',
      width: '343px',
      '&& .MuiFormLabel-root.Mui-error': {
        color: colors.red,
      },
      '&& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.red,
      },
      '& .MuiFormHelperText-root.Mui-error': {
        color: colors.red,
      },
    },
  }
);

export const SPWFormText = styled(Box)`
  && {
    width: 343px;
    font-family: Roboto;
    font-style: normal;
    font-size: 16px;
    line-height: 20px;
    color: #000000;
    margin-bottom: 44px;
  }
`;

export const SPWCityInput = styled(SPWInput)`
  && {
    width: 240px;
  }
`;

interface ISPWStateInput {
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  value: string;
}
export const SPWStateInput = withStyles(
  (props: ISPWStateInput & SelectProps) => (
    <FormControl variant="outlined">
      <InputLabel id="spw-state-input">State</InputLabel>
      <Select
        {...props}
        label="state"
        labelId="spw-state-input"
        renderValue={(value: string) => value}
        MenuProps={{ disableScrollLock: true }}
      >
        {US_STATES.map((usState) => (
          <MenuItem value={usState.code} key={usState.code}>
            {usState.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ),
  {
    root: {
      width: 90,
    },
  }
);

interface SPWScenarioSelectProps {
  label: string;
}

export const SPWScenarioSelect = withStyles(
  (props: SelectProps & SPWScenarioSelectProps) => {
    return (
      <>
        <InputLabel style={{ background: 'white' }}>{props.label}</InputLabel>
        <Select
          variant="outlined"
          {...(omit(['label'], props) as Omit<
            SelectProps & SPWScenarioSelectProps,
            'label'
          >)}
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={{ disableScrollLock: true }}
        />
      </>
    );
  },
  {
    root: {
      borderRadius: '4px',
      border: `1px solid ${colors.selectBorder}`,
      padding: '12px 15px 8px 12px',
      fontSize: '14px',
      background: 'white',
      lineHeight: 1.8,
      width: '100%',
      zIndex: 0,
    },
    select: {
      '&:focus': {
        border: `1px solid ${colors.selectBorder}`,
        background: 'none',
        borderRadius: '4px',
      },
    },
  }
);

export const SimpleUSFInput = styled(BaseTextField)`
  && {
    width: 100%;

    .MuiInputLabel-outlined.MuiInputLabel-shrink {
      color: ${colors.fontColor};
      white-space: nowrap;
    }

    .MuiInputBase-input.MuiOutlinedInput-input::placeholder {
      font-style: normal;
    }
  }
`;

export const FormBox = withStyles(Box, {
  root: {
    marginBottom: '20px',
  },
});

export const FormBoxMedium = withStyles(Box, {
  root: {
    marginBottom: '32px',
  },
});

export const FormBoxSmall = withStyles(Box, {
  root: {
    marginBottom: '20px',
  },
});

export const SPWHeaderTitle = styled(Box)`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 32px;
  /* identical to box height, or 133% */
  color: #000000;
  flex: 0;
  flex-basis: auto;
`;

const noBorderBottom = {
  content: '""',
  borderBottom: 'none',
};

export const SelectBox = withStyles(Box, (_theme, props) => ({
  root: {
    position: 'relative',
    '& .MuiInput-underline:before': noBorderBottom,
    '& .MuiInput-underline:after': noBorderBottom,
    '& .MuiInput-underline:hover': noBorderBottom,
    '& .Mui-focused .MuiSelect-select:focus': {
      background: 'white',
    },
    '& .MuiSelect-outlined.MuiSelect-outlined': {
      paddingLeft: 32,
    },
    '& label.Mui-focused': {
      color: '#616161',
    },
    '&& label': {
      color: '#616161',
      fontSize: '14px',
      fontWeight: 'bold',
      transform: 'translate(0, 2px) scale(0.75)',
    },
    '&& label.MuiInputLabel-shrink': {
      position: 'absolute',
      transform: 'translate(0, 2px) scale(0.75)',
      background: 'white',
      padding: '0 8px',
      zIndex: 1,
    },
    '&& .bubble': {
      width: 18,
      height: 18,
      position: 'absolute',
      zIndex: 1,
      top: 11,
      left: 16,
      borderRadius: 16,
      background: props.color as string,
    },
    '& .MuiMenu-paper': {
      left: '24px !important',
      width: 462,
    },
  },
}));

interface SPWSpaceTypeSelectProps {
  currentcategory: Category | Subcategory;
  spaceTypeCategoriesMap?: SpaceTypeCategoryMap;
  bubblecolor?: string;
  disabled?: boolean;
}
export const SPWSpaceTypeSelect = withStyles(
  (props: SPWSpaceTypeSelectProps & SelectProps) => {
    const {
      currentcategory,
      spaceTypeCategoriesMap,
      bubblecolor,
      disabled,
    } = props;
    const [category, setCategory] = useState(currentcategory);
    const [isOpen, setIsOpen] = useState(false);
    const spaceTypeSelectContainer = useRef();
    const selectProps = omit(['onChange', 'spaceTypeCategoriesMap'], props);
    const handleCategory = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isNil(e.target.value)) {
        return;
      }
      if (!isNil(spaceTypeCategoriesMap)) {
        const spaceTypeId = parseInt(e.target.value, 10);
        const selectedCat = categoryBySpaceTypeId(
          spaceTypeId,
          spaceTypeCategoriesMap
        );
        if (!isNil(selectedCat)) {
          setCategory(selectedCat as Category);
        }
      }
      props.onChange(e, undefined);
    };

    const handleClickAway: ComponentProps<
      typeof ClickAwayListener
    >['onClickAway'] = (e) => {
      const toggleClicked = e
        .composedPath()
        .includes(document.getElementById(CLIENT_NAMES_TOGGLE_CONTAINER_ID));

      if (!toggleClicked) {
        setIsOpen(false);
      }
    };

    return (
      <SelectBox
        id="the-select-box"
        ref={spaceTypeSelectContainer}
        color={bubblecolor}
        onClick={() => setIsOpen((prev) => !prev && !disabled)}
      >
        <InputLabel id={`select-label-${category}`} />
        {bubblecolor && <div className={`bubble ${category}`} />}
        <ClickAwayListener onClickAway={handleClickAway}>
          <Select
            IconComponent={disabled ? DisabledChevron : DefaultChevron}
            variant="outlined"
            onChange={handleCategory}
            {...selectProps}
            style={{ width: '100%', borderRadius: 0 }}
            MenuProps={{
              disableScrollLock: true,
              open: isOpen,
              container() {
                return spaceTypeSelectContainer.current;
              },
              classes: {
                list: 'space-type-dropdown-list',
              },
            }}
          />
        </ClickAwayListener>
      </SelectBox>
    );
  },
  {
    root: {
      background: 'white',
      border: '1px solid #F5F5F5',
      borderRadius: '8px',
      width: '100%',
      lineHeight: '20px',
      zIndex: 0,
      padding: 9,
      fontSize: 14,
      '& .MuiSelect-outlined': {
        padding: 0,
      },
    },
    '.MuiMenuItem-root': {
      display: 'flex !important',
    },
  }
);

export const TextareaBox = withStyles(Box, {
  root: {
    '& label.Mui-focused': {
      color: '#616161',
    },
    '&& label': {
      color: '#616161',
      fontSize: '14px',
      fontWeight: 'bold',
      transform: 'translate(0, 2px) scale(0.75)',
    },
    '&& label.MuiInputLabel-shrink': {
      transform: 'translate(0, 2px) scale(0.75)',
    },
    '&& textarea': {
      background: '#F5F5F5',
      border: '1px solid #F5F5F5',
      borderRadius: '8px',
      height: '80px',
      minWidth: '343px',
      maxWidth: '100%',
      width: '100%',
      color: 'rgba(0, 0, 0, 0.87)',
      cursor: 'text',
      fontSize: '1rem',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      fontWeight: '400',
      lineHeight: '1.1876em',
      letterSpacing: '0.00938em',
    },
  },
});

interface SPWTextareaProps {
  label: string;
}
export const SPWTextareaAutosize = (
  props: SPWTextareaProps & TextareaAutosizeProps
) => {
  const { label } = props;
  return (
    <TextareaBox>
      <InputLabel id={`text-area-label-${label}`}>{label}</InputLabel>
      <TextareaAutosize {...props} />
    </TextareaBox>
  );
};

interface InputContainerProps {
  ratio?: boolean;
  percentage?: boolean;
  fontColor?: string;
}

export const InputContainer = styled('div')<InputContainerProps>`
  && {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    input.MuiOutlinedInput-input {
      ${(props) => (props.ratio ? 'padding-left: 33px' : '')}
    }
  }
`;

export const RsfLoadFactorInput = (
  props: TextFieldProps & {
    setInputState: (input: string) => void;
    clearDefaultValue?: boolean;
    value: string;
    max?: number;
  }
) => {
  const validRegex = /^[0-9.]+$/;
  const {
    setInputState,
    clearDefaultValue,
    value,
    onBlur,
    max = 100,
    ...rest
  } = props;
  const [displayedValue, setDisplayedValue] = useState(
    parseFloat(value).toFixed(2)
  );
  const [isFocused, setIsFocused] = useState(false);

  const handlePercentInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (validRegex.test(e.target.value) || e.target.value === '') {
      const newValue =
        parseFloat(e.target.value) > max ? max.toFixed(2) : e.target.value;
      setInputState(newValue);
      setDisplayedValue(newValue);
    }
  };

  const handlePercentInputOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    const percent = parseFloat(value);
    if (
      value === '' ||
      percent <= 0 ||
      Number.isNaN(percent) ||
      !Number.isFinite(percent)
    ) {
      setInputState('0.00');
      setDisplayedValue('--');
    } else {
      setInputState(value);
      setDisplayedValue(parseFloat(value).toFixed(2));
    }
    if (onBlur) onBlur(e);
  };

  useEffect(() => {
    // This is to update the displayed load factor when either the usf or rsf change
    if (!isFocused) {
      const percent = parseFloat(value);
      setDisplayedValue(
        Number.isNaN(percent) || !Number.isFinite(percent) || percent <= 0
          ? '--'
          : parseFloat(value).toFixed(2)
      );
    }
  }, [value, isFocused]);

  return (
    <InputContainer percentage>
      <SimpleUSFInput
        {...rest}
        value={displayedValue}
        InputLabelProps={globalInputLabelProps}
        variant="outlined"
        type="string"
        onChange={handlePercentInputChange}
        onFocus={() => {
          setIsFocused(true);
          if (
            clearDefaultValue &&
            (parseFloat(props.value) === 0 || displayedValue === '--')
          ) {
            setDisplayedValue('');
          }
        }}
        onBlur={handlePercentInputOnBlur}
        InputProps={{
          endAdornment: (
            <InputAdornment style={{ color: colors.mainGray }} position="end">
              %
            </InputAdornment>
          ),
        }}
      />
    </InputContainer>
  );
};
export const SPWFlexWidthInput = withStyles(
  (props: TextFieldProps) => (
    <TextField
      {...props}
      InputLabelProps={globalInputLabelProps}
      variant="outlined"
    />
  ),
  {
    root: {
      height: '40px',
      width: '100%',
    },
  }
);

interface IUsfWithCommasInput {
  setUsfAsNumber?: (usf: number) => void;
  value: number;
  max?: number;
  min?: number;
  clearDefaultValue?: boolean;
  dataTestId?: string;
  placeholder?: string;
  shouldUseDashesAsDefault?: boolean;
  clearOnNull?: boolean;
}

export const UsfWithCommasInput = (
  props: TextFieldProps & IUsfWithCommasInput
) => {
  const {
    value,
    setUsfAsNumber,
    max,
    min = 0,
    clearDefaultValue = false,
    clearOnNull = false,
    label,
    disabled,
    dataTestId,
    placeholder,
    shouldUseDashesAsDefault = true,
    onBlur,
  } = props;

  const [usfWithCommas, setUsfWithCommas] = useState(
    !shouldUseDashesAsDefault && !value ? '' : asUsf(value)
  );
  const [usfNumber, setUsfNumber] = useState(value);

  useEffect(() => {
    if (value > 0 || (shouldUseDashesAsDefault && value === null)) {
      setUsfWithCommas(!value && placeholder ? '' : asUsf(value));
      setUsfNumber(value);
    } else if (clearOnNull && value === null) {
      setUsfWithCommas('');
      setUsfNumber(value);
    }
  }, [value, placeholder, shouldUseDashesAsDefault, clearOnNull]);

  const usfWithCommasToUsfNumber = (usfString: string) => {
    const removeCommasRegEx = /,/g;
    const usfNoCommas = usfString.replace(removeCommasRegEx, '');
    const usfToNumber = parseFloat(usfNoCommas);
    return usfToNumber;
  };

  const addCommasToUsfString = (usfValue: string) => {
    if (parseInt(usfValue, 10) > 0) {
      const getUsfAsNumber = _.min([
        usfWithCommasToUsfNumber(usfValue),
        max || MAX_BUILDING_USF,
      ]);
      setUsfWithCommas(asUsf(getUsfAsNumber));
      setUsfNumber(getUsfAsNumber);
      if (setUsfAsNumber) setUsfAsNumber(getUsfAsNumber);
    } else {
      setUsfWithCommas('');
      setUsfNumber(0);
      if (setUsfAsNumber) setUsfAsNumber(0);
    }
  };

  return (
    <SimpleUSFInput
      type="string"
      label={label}
      value={usfWithCommas}
      disabled={disabled}
      data-testid={dataTestId}
      placeholder={placeholder}
      onFocus={() => {
        if (clearDefaultValue && usfWithCommas === '--') {
          setUsfWithCommas('');
        }
      }}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        addCommasToUsfString(event.target.value)
      }
      onBlur={(e) => {
        if (setUsfAsNumber) setUsfAsNumber(Math.max(usfNumber || 0, min));
        if (onBlur) onBlur(e);
        if (clearDefaultValue && usfWithCommas === '') {
          if (shouldUseDashesAsDefault || min > 0) setUsfWithCommas(asUsf(min));
          setUsfNumber(min);
        }
      }}
    />
  );
};
